import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiErrorResponse, ApiResponse, ApiServiceInterface, FormOptions, FormServiceInterface } from '@hutsix/ngxh6';
import { PermitViewGrantService } from '../../../services/permit-view-grant.service';

interface ResubmitResult {
    permitType: string;
    permitNumber: string;
    status: string;
    token: string;
    viewGrant?: string;
    viewGrantExpiresAt?: number;
}

/**
 * "More information requested" edit flow. The applicant arrives from the emailed
 * link (/permit/edit/:editToken). We show what staff asked for, gate the form
 * behind an email confirmation, then render their original application form
 * pre-filled - submitting it to the single-use resubmit endpoint sends the
 * application back to review.
 */
@Component({
    selector: 'app-edit-permit',
    templateUrl: './edit-permit.component.html',
    styleUrls: ['./edit-permit.component.scss'],
    standalone: false,
})
export class EditPermitComponent implements OnInit {
    public editToken?: string;
    public loading = true;
    public gone = false;
    public notFound = false;

    // Stub shown before unlocking
    public permitType?: string;
    public status?: string;
    public needsInfoNote?: string | null;

    // Email-confirm gate
    public locked = true;
    public email = '';
    public verifying = false;
    public verifyError?: string;

    // Unlocked edit form
    public formOpts?: FormOptions;
    public editData?: any;

    // Success
    public result?: ResubmitResult | null;

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        @Inject('FormService') private formService: FormServiceInterface,
        private route: ActivatedRoute,
        private grants: PermitViewGrantService,
    ) {}

    ngOnInit(): void {
        this.editToken = this.route.snapshot.paramMap.get('editToken') || undefined;
        this.api
            .get({
                url: `/api/public/permit/edit/${this.editToken}`,
                skipAuth: true,
                bypassOfflineQueue: true,
                useCache: false,
                displayErrors: false,
            })
            .subscribe(
                (res: ApiResponse<any>) => {
                    this.loading = false;
                    this.permitType = res.data?.permitType;
                    this.status = res.data?.status;
                    // needsInfoNote is deliberately NOT read here - it may contain
                    // personal details and is only returned after the email gate.
                },
                (err: ApiErrorResponse<any>) => {
                    this.loading = false;
                    if (err?.error?.status === 410) {
                        this.gone = true;
                    } else {
                        this.notFound = true;
                    }
                },
            );
    }

    public verify(): void {
        if (!this.email || this.verifying) {
            return;
        }
        this.verifying = true;
        this.verifyError = undefined;
        this.api
            .post({
                url: `/api/public/permit/edit/${this.editToken}/verify`,
                data: { email: this.email },
                skipAuth: true,
                displayErrors: false,
            })
            .subscribe(
                (res: ApiResponse<any>) => {
                    this.verifying = false;
                    this.needsInfoNote = res.data?.needsInfoNote;
                    this.editData = res.data?.editData;
                    this.formOpts = this.buildFormOpts(res.data?.formSlug);
                    this.locked = false;
                },
                (err: ApiErrorResponse<any>) => {
                    this.verifying = false;
                    if (err?.error?.status === 410) {
                        this.gone = true;
                        this.locked = false;
                    } else {
                        this.verifyError =
                            err?.error?.status === 403
                                ? "That email doesn't match this application. Please use the email address you applied with."
                                : 'Sorry, something went wrong. Please try again.';
                    }
                },
            );
    }

    /**
     * Clone the original application form's options and re-point its POST at the
     * single-use resubmit endpoint. allow_edits stays false, so FormObject posts
     * to api_post as-is (no id appended).
     */
    private buildFormOpts(formSlug?: string): FormOptions {
        const base = this.formService.getForm(formSlug as any);
        return {
            ...base,
            public: true,
            allow_edits: false,
            api_post: `/api/public/permit/edit/${this.editToken}/resubmit`,
            submit_label: 'Resubmit application',
        };
    }

    public onResubmitted(res: ApiResponse<ResubmitResult>): void {
        // Only switch to the success panel on a real success; a validation error
        // (no status) must leave the form in place to show its inline errors.
        if (res?.data?.status) {
            this.result = res.data;
            // They confirmed their email to get into this form - carry it through so
            // the "view your permit" link doesn't gate them again.
            this.grants.remember(res.data);
        }
    }
}
